import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  VStack,
  Text,
  Alert,
  AlertIcon,
  Checkbox,
} from '@chakra-ui/react'
import { Eye, EyeOff, Lock, User } from 'lucide-react'
import { loginSchema, type LoginFormData } from '@/lib/validations/auth'
import { useAuthStore } from '@/store/auth'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useSiteSettings } from '@/hooks/useSiteSettings'

export function LoginForm() {
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const navigate = useNavigate()
  const { login, isLoading } = useAuthStore()
  const { settings: siteSettings } = useSiteSettings()
  const colors = useThemeColors()

  const siteName = import.meta.env.VITE_APP_NAME || 'Sign In'
  const siteDescription =
    import.meta.env.VITE_APP_DESCRIPTION || 'Enter your credentials to access your account'

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  // Prefill client from query param if present and show setup messages
  const [searchParams] = useSearchParams()
  React.useEffect(() => {
    const client = searchParams.get('client')
    const setup = searchParams.get('setup')
    const already = searchParams.get('already_activated')

    if (client) {
      setValue('user_login', client)
    }

    if (setup === 'success') {
      setInfo('Your password has been set. Please sign in with your credentials.')
    } else if (already === '1') {
      setInfo('Your account has already been set up. Please sign in.')
    }
  }, [searchParams, setValue])

  const onSubmit = async (data: LoginFormData) => {
    try {
      setError('')
      console.log('[LoginForm] Starting login for:', data.user_login)

      const loginData = {
        ...data,
        remember_me: rememberMe,
      }

      const result = await login(loginData)

      if (result.success) {
        navigate('/dashboard')
      } else if (result.twoFactorSetupRequired) {
        const verifyEmail = result.twoFactorEmail
        if (!verifyEmail) {
          setError('Unable to start 2FA setup: missing email from server. Please try again.')
          return
        }
        window.location.href = `/two-factor/setup?email=${encodeURIComponent(
          verifyEmail
        )}&remember_me=${rememberMe ? '1' : '0'}`
      } else if (result.twoFactorRequired) {
        const verifyEmail = result.twoFactorEmail
        if (!verifyEmail) {
          setError('Unable to verify login: missing email from server. Please try again.')
          return
        }
        window.location.href = `/two-factor?email=${encodeURIComponent(
          verifyEmail
        )}&remember_me=${rememberMe ? '1' : '0'}`
      } else {
        setError(result.message || 'Login failed')
      }
    } catch (err: any) {
      if (err.response?.status === 429) {
        const minutes = err.response?.data?.lockout_remaining_minutes
        setError(
          minutes
            ? `Too many login attempts. Try again in ${minutes} minute(s).`
            : 'Too many login attempts. Please try again later.'
        )
      } else {
        setError(err.response?.data?.message || 'Login failed')
      }
    }
  }

  return (
    <Box minH="100vh" bg={colors.bg} display="flex" alignItems="center" justifyContent="center">
      <Card maxW="md" w="full" bg={colors.cardBg}>
        <CardBody p={6}>
          <VStack spacing={4}>
            <Box textAlign="center">
              <Box mb={2} display="flex" justifyContent="center">
                <img
                  src={siteSettings.auth_logo || '/assets/img/als-logo-2.png'}
                  alt={siteName}
                  style={{ maxWidth: '300px', height: 'auto' }}
                />
              </Box>

              <Heading size="lg" color={colors.heading} mb={1}>
                {siteName}
              </Heading>
              <Text color={colors.text} fontSize="sm">
                {siteDescription}
              </Text>
            </Box>

            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {error}
              </Alert>
            )}

            {info && (
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                {info}
              </Alert>
            )}

            <Box as="form" onSubmit={handleSubmit(onSubmit)} w="100%">
              <VStack spacing={3} w="100%">
                <FormControl isInvalid={!!errors.user_login} isDisabled={isLoading}>
                  <FormLabel>Client ID</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <User size={20} />
                    </InputLeftElement>
                    <Input
                      {...register('user_login')}
                      type="text"
                      placeholder="Enter your Client ID"
                      pl="10"
                      isDisabled={isLoading}
                    />
                  </InputGroup>
                  <FormErrorMessage>{errors.user_login?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.password} isDisabled={isLoading}>
                  <FormLabel>Password</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Lock size={20} />
                    </InputLeftElement>
                    <Input
                      {...register('password')}
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      pl="10"
                      isDisabled={isLoading}
                    />
                    <InputRightElement width="3rem">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPassword(!showPassword)}
                        type="button"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        h="1.75rem"
                        minW="1.75rem"
                        p={0}
                        isDisabled={isLoading}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
                </FormControl>

                <Box
                  mt={1}
                  w="100%"
                  display="grid"
                  gridTemplateColumns="1fr 1fr"
                  gap={3}
                  alignItems="center"
                >
                  <Box textAlign="left">
                    <Checkbox
                      isChecked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      colorScheme="blue"
                      size="sm"
                      isDisabled={isLoading}
                    >
                      <Text fontSize="sm" color={colors.link}>
                        Remember me
                      </Text>
                    </Checkbox>
                  </Box>
                  <Box textAlign="right">
                    <Button
                      variant="link"
                      color="blue.500"
                      fontSize="sm"
                      onClick={() => navigate('/forgot-password')}
                      _hover={{ textDecoration: 'underline' }}
                      isDisabled={isLoading}
                    >
                      Forgot your password?
                    </Button>
                  </Box>
                </Box>

                <Button
                  type="submit"
                  bg="blue.500"
                  color="white"
                  _hover={{ bg: 'blue.600' }}
                  size="lg"
                  w="full"
                  isLoading={isLoading}
                  loadingText="Signing in..."
                  isDisabled={isLoading}
                >
                  Sign In
                </Button>
              </VStack>
            </Box>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  )
}
